import { Chain } from '@ankr.com/chains-list';

import { AccountPermission } from 'modules/permissions/constants';
import { hasEvmSubchains } from 'modules/chains/utils/hasEvmSubchains';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useCheckAccountPermission } from 'modules/account/hooks/useCheckAccountPermission';
import { useDialog } from 'modules/common/hooks/useDialog';

import { useAddNetworkHandler } from './useAddNetworkHandler';

interface UseAddNetworkButtonProps {
  chain: Chain;
  hasAddNetworkDialog?: boolean;
  onClick?: () => void;
}

export const useAddNetworkButton = ({
  chain,
  hasAddNetworkDialog,
  onClick: onClickExternal,
}: UseAddNetworkButtonProps) => {
  const { isFreemium } = useAccountStatus();

  const hasBillingRoleAccess = useCheckAccountPermission({
    permission: AccountPermission.Billing,
  });

  const addNetwork = useAddNetworkHandler({ chain });

  const {
    isOpened: isAddNetworkDialogOpened,
    onClose: closeAddNetworkDialog,
    onOpen: openAddNetworkDialog,
  } = useDialog();

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    /* stop propagation for click event to avoid parent element click */
    event.preventDefault();
    event.stopPropagation();

    if (onClickExternal) {
      return onClickExternal();
    }

    if (hasAddNetworkDialog) {
      return openAddNetworkDialog();
    }

    return addNetwork?.();
  };

  const hasToUpgradeButNoAccess =
    chain.premiumOnly && isFreemium && !hasBillingRoleAccess;

  const isHidden =
    (!addNetwork && !hasAddNetworkDialog) ||
    !hasEvmSubchains(chain) ||
    hasToUpgradeButNoAccess;

  return {
    addNetwork,
    isHidden,
    onClick,
    isAddNetworkDialogOpened,
    closeAddNetworkDialog,
  };
};
